import { useMemo, useCallback } from 'react';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';

export interface PermissionContext {
  // Document permissions
  canViewDocuments: boolean;
  canCreateDocuments: boolean;
  canEditDocuments: boolean;
  canDeleteDocuments: boolean;
  /** Can reach the manager-gated delete API; document-level auth still applies. */
  canRequestDocumentDelete: boolean;
  canShareDocuments: boolean;

  // User management permissions
  canManageUsers: boolean;
  canInviteUsers: boolean;
  canEditUserRoles: boolean;

  // System permissions
  canAccessAdmin: boolean;
  canManageSettings: boolean;
  canViewAuditLogs: boolean;
  canManageIntegrations: boolean;

  // Helper functions
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  canPerformAction: (resource: string, action: string) => boolean;
}

// Define permission constants
export const PERMISSIONS = {
  // Document permissions
  DOCUMENT_VIEW: 'document:view',
  DOCUMENT_CREATE: 'document:create',
  DOCUMENT_EDIT: 'document:edit',
  DOCUMENT_DELETE: 'document:delete',
  DOCUMENT_SHARE: 'document:share',

  // User permissions
  USER_MANAGE: 'user:manage',
  USER_INVITE: 'user:invite',
  USER_EDIT_ROLES: 'user:edit_roles',

  // System permissions
  ADMIN_ACCESS: 'admin:access',
  SETTINGS_MANAGE: 'settings:manage',
  AUDIT_VIEW: 'audit:view',
  INTEGRATION_MANAGE: 'integration:manage',
} as const;

// Role-based permission mappings
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    PERMISSIONS.DOCUMENT_VIEW,
    PERMISSIONS.DOCUMENT_CREATE,
    PERMISSIONS.DOCUMENT_EDIT,
    PERMISSIONS.DOCUMENT_DELETE,
    PERMISSIONS.DOCUMENT_SHARE,
    PERMISSIONS.USER_MANAGE,
    PERMISSIONS.USER_INVITE,
    PERMISSIONS.USER_EDIT_ROLES,
    PERMISSIONS.ADMIN_ACCESS,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.INTEGRATION_MANAGE,
  ],
  manager: [
    PERMISSIONS.DOCUMENT_VIEW,
    PERMISSIONS.DOCUMENT_CREATE,
    PERMISSIONS.DOCUMENT_EDIT,
    PERMISSIONS.DOCUMENT_SHARE,
    PERMISSIONS.USER_INVITE,
    PERMISSIONS.AUDIT_VIEW,
  ],
  user: [
    PERMISSIONS.DOCUMENT_VIEW,
    PERMISSIONS.DOCUMENT_CREATE,
  ],
  viewer: [
    PERMISSIONS.DOCUMENT_VIEW,
  ],
};

export const usePermissions = (): PermissionContext => {
  const user = useAppSelector(selectUser);

  // Get user's effective permissions
  const userPermissions = useMemo(() => {
    if (!user) return new Set<string>();

    const permissions = new Set<string>();

    // Add role-based permissions
    // Check both 'role' (single string) and 'roles' (array) for compatibility
    const userRoles: string[] = [];

    // Add role from 'role' property if it exists
    if (user.role) {
      userRoles.push(user.role);
    }

    // Add roles from 'roles' array if it exists
    if (user.roles && Array.isArray(user.roles)) {
      user.roles.forEach(r => {
        if (!userRoles.includes(r)) {
          userRoles.push(r);
        }
      });
    }

    // Apply permissions for all roles
    userRoles.forEach(role => {
      if (ROLE_PERMISSIONS[role]) {
        ROLE_PERMISSIONS[role].forEach(permission => permissions.add(permission));
      }
    });

    // Add explicit user permissions
    if (user.permissions) {
      user.permissions.forEach(permission => permissions.add(permission));
    }

    // Remove any explicitly denied permissions
    if (user.deniedPermissions) {
      user.deniedPermissions.forEach(permission => permissions.delete(permission));
    }

    return permissions;
  }, [user]);

  // Permission check functions
  const hasPermission = useCallback((permission: string): boolean => {
    return userPermissions.has(permission);
  }, [userPermissions]);

  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    return permissions.some(permission => userPermissions.has(permission));
  }, [userPermissions]);

  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    return permissions.every(permission => userPermissions.has(permission));
  }, [userPermissions]);

  const canPerformAction = useCallback((resource: string, action: string): boolean => {
    const permission = `${resource}:${action}`;
    return userPermissions.has(permission);
  }, [userPermissions]);

  // The delete route requires manager or admin before it evaluates ownership
  // and per-document grants. Keep this separate from the global delete
  // permission so a manager with an individual grant retains a UI path.
  const canRequestDocumentDelete = useMemo(() => {
    if (!user) return false;
    const roles = new Set([user.role, ...(user.roles ?? [])]);
    return roles.has('admin') || roles.has('manager');
  }, [user]);

  // Create the permission context
  const permissionContext: PermissionContext = useMemo(() => ({
    // Document permissions
    canViewDocuments: hasPermission(PERMISSIONS.DOCUMENT_VIEW),
    canCreateDocuments: hasPermission(PERMISSIONS.DOCUMENT_CREATE),
    canEditDocuments: hasPermission(PERMISSIONS.DOCUMENT_EDIT),
    canDeleteDocuments: hasPermission(PERMISSIONS.DOCUMENT_DELETE),
    canRequestDocumentDelete,
    canShareDocuments: hasPermission(PERMISSIONS.DOCUMENT_SHARE),

    // User management permissions
    canManageUsers: hasPermission(PERMISSIONS.USER_MANAGE),
    canInviteUsers: hasPermission(PERMISSIONS.USER_INVITE),
    canEditUserRoles: hasPermission(PERMISSIONS.USER_EDIT_ROLES),

    // System permissions
    canAccessAdmin: hasPermission(PERMISSIONS.ADMIN_ACCESS),
    canManageSettings: hasPermission(PERMISSIONS.SETTINGS_MANAGE),
    canViewAuditLogs: hasPermission(PERMISSIONS.AUDIT_VIEW),
    canManageIntegrations: hasPermission(PERMISSIONS.INTEGRATION_MANAGE),

    // Helper functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canPerformAction,
  }), [hasPermission, hasAnyPermission, hasAllPermissions, canPerformAction, canRequestDocumentDelete]);

  return permissionContext;
};
